import { boxFixedPriceCentsOf, formatCents, parseMoneyToCents, tt } from '@agos/shared'

/**
 * The burger-box assembler — the third thing `/new-order` had the pieces for and was not offering.
 *
 * `GET /burger-box-config` answers one box in this data: **Burger Box**, `triggerCategoryId: 3`, four
 * slots — Burger (category 3), Príloha (9), Dressing (11), nápoje (10) — every one with
 * `presetItems: []` and, notably, **`required: false`**. The cart already renders a box and removes it as
 * a unit (`boxGroupId`, `removeBoxGroup`); nothing created one.
 *
 * **A box is priced as the sum of what is in it, unless the owner has given it a fixed price.** Legacy
 * sums the selected product in each slot plus that slot's fixed preset items, and that is still the
 * default. §10A.107 added `fixedPrice` to the config - the owner's item E, *"ak dám že stojí 7.50 tak
 * bude stáť toľko"* - and `ServerPricing` is what applies it. This screen reads it so the till shows
 * the same number the bill will carry; it never decides the price.
 *
 * **Every slot being optional is the interesting part.** Legacy's guard is
 * `!s.required || selections[s.id] != null || fixed.length > 0`, which with no required slots is
 * *always true* — so *Pridať box* is enabled on a box containing nothing, and adds a group of zero lines
 * to the cart. That is {@link boxProblems}' first rule: a box has to contain something, whatever the
 * config says about required.
 *
 * **Preset items come in two kinds.** `presetItems[].fixed` is always included and cannot be deselected;
 * the rest are the slot's offer alongside whatever the category holds. Both are empty in this data, so
 * the handling is written from the legacy code rather than measured, and says so.
 */

export interface BoxSlot {
  id: string
  label: string
  categoryId: number | null
  required: boolean
  fixedProductIds: number[]
  presetProductIds: number[]
}

export interface BurgerBox {
  id: string
  name: string
  description: string | null
  slots: BoxSlot[]
  hideProductImages: boolean
  /**
   * Sold for this, whatever its parts cost - §10A.107, in cents.
   *
   * `null` for every box in the owner's data today. The server is the authority; this is read so the
   * till shows what the bill will say.
   */
  fixedPriceCents: number | null
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

function ids(presets: unknown, fixed: boolean): number[] {
  if (!Array.isArray(presets)) return []
  return presets.flatMap((entry) => {
    const row = entry as Record<string, unknown>
    if ((row.fixed === true) !== fixed) return []
    return typeof row.productId === 'number' ? [row.productId] : []
  })
}

/**
 * The box configured for a category — §9.46, and what the customer app has always done.
 *
 * A config carries `triggerCategoryId`, and `customer-menu`'s `boxFor` picks the box belonging to the
 * category being browsed. This module read *the first active config* and never looked at the trigger
 * at all. With one box those are the same row; with a second — a salad box on the salads category,
 * which is what the field is for — the manager would offer the burger box under both while the
 * customer app offered each correctly, and a waitress would build the wrong one.
 *
 * **No category selected falls back to the first active box.** *Všetko* is not a category, and
 * hiding the box there would leave it unreachable until somebody guessed which category to open.
 */
/**
 * Every switched-on box this category offers, in the server's order — §9.209.
 *
 * **This ended in `.find`, which was the owner's report one app over.** §9.200 fixed the customer
 * app's copy and this one kept it: the payload has always been an array, so a second active box on
 * the same category was fetched, parsed, held and never drawn — on the screen a waiter takes an
 * order on.
 */
export function boxesForCategory(payload: unknown, categoryId: number | null): BurgerBox[] {
  if (!Array.isArray(payload)) return []
  if (categoryId === null) {
    const only = activeBoxOf(payload)
    return only === null ? [] : [only]
  }
  return (payload as Record<string, unknown>[])
    .filter((row) => row.active === true && row.triggerCategoryId === categoryId)
    .map((row) => activeBoxOf([row]))
    .filter((box): box is BurgerBox => box !== null)
}

/**
 * A box by its own id, from anywhere in the payload - §9.331.
 *
 * **The owner:** *"when I opened stundentský burger box, it opened normal burger box"*, and this is
 * the lookup that made it happen. The panel resolved its box out of {@link boxesForCategory}, and
 * `openPanel` clears the active category on the same click that chose the box - so by the time the
 * panel rendered, that list held only *the first active box in the payload* and the chosen id
 * matched nothing. The `?? boxes[0]` fallback beside it then did exactly what it says.
 *
 * The fallback is right for what it was written for (§9.209: an id belonging to the category you
 * just left). It is wrong as the answer to *which box did they click*, which is what this is for: an
 * id is unambiguous and does not depend on what the category chips are doing.
 */
export function boxById(payload: unknown, id: string | null): BurgerBox | null {
  if (id === null || !Array.isArray(payload)) return null
  const row = (payload as Record<string, unknown>[]).find(
    (entry) => entry.active === true && text(entry.id) === id,
  )
  return row === undefined ? null : activeBoxOf([row])
}

/** The first of them, for a caller that wants one answer. */
export function boxForCategory(payload: unknown, categoryId: number | null): BurgerBox | null {
  return boxesForCategory(payload, categoryId)[0] ?? null
}

/** The switched-on box, or none. Legacy renders one box; the endpoint answers a list. */
export function activeBoxOf(payload: unknown): BurgerBox | null {
  if (!Array.isArray(payload)) return null
  const row = payload.find((entry) => (entry as Record<string, unknown>).active === true) as
    | Record<string, unknown>
    | undefined
  if (row === undefined) return null
  const slots = Array.isArray(row.slots) ? row.slots : []
  return {
    id: text(row.id) ?? 'box',
    name: text(row.name) ?? tt('Box'),
    description: text(row.description),
    hideProductImages: row.hideProductImages === true,
    // §10A.107 - `null` for every box today, which is what keeps `boxTotalCents` on its old path.
    fixedPriceCents: boxFixedPriceCentsOf(row.fixedPrice),
    slots: slots.flatMap((entry) => {
      const slot = entry as Record<string, unknown>
      const id = text(slot.id)
      if (id === null) return []
      return [
        {
          id,
          label: text(slot.label) ?? id,
          categoryId: typeof slot.categoryId === 'number' ? slot.categoryId : null,
          required: slot.required === true,
          fixedProductIds: ids(slot.presetItems, true),
          presetProductIds: ids(slot.presetItems, false),
        },
      ]
    }),
  }
}

export interface BoxProduct {
  id: number
  name: string
  priceCents: number
  categoryId: number | null
}

/**
 * What may go in a slot.
 *
 * Its preset items where it names any, otherwise everything in its category — legacy's rule. A slot with
 * neither a category nor presets offers nothing, and the panel says so rather than showing an empty list
 * that looks like a failed load.
 */
export function choicesForSlot(slot: BoxSlot, products: readonly BoxProduct[]): BoxProduct[] {
  if (slot.presetProductIds.length > 0) {
    return products.filter((product) => slot.presetProductIds.includes(product.id))
  }
  if (slot.categoryId === null) return []
  return products.filter((product) => product.categoryId === slot.categoryId)
}

export function fixedForSlot(slot: BoxSlot, products: readonly BoxProduct[]): BoxProduct[] {
  return products.filter((product) => slot.fixedProductIds.includes(product.id))
}

export type Selections = Record<string, number | null>

/** Everything that would go into the cart, fixed items first, in slot order. */
export function boxLines(
  box: BurgerBox,
  selections: Selections,
  products: readonly BoxProduct[],
): { slot: BoxSlot; product: BoxProduct }[] {
  return box.slots.flatMap((slot) => {
    const chosenId = selections[slot.id] ?? null
    const chosen =
      chosenId === null ? [] : products.filter((product) => product.id === chosenId).slice(0, 1)
    return [...fixedForSlot(slot, products), ...chosen].map((product) => ({ slot, product }))
  })
}

// A plain integer reduce: these are already cents (FB-056).
/**
 * What the box costs on the till - §10A.107.
 *
 * The sum of its parts, or the owner's fixed price when the box has one. **The screen has to agree
 * with `ServerPricing`**, which applies the same fixed price from the same stored configuration: a
 * till showing 8,50 on an order the server bills at 7,50 is the mismatch §10A.80 was about, and a
 * waiter reading the wrong total to a customer on the phone is worse than a wrong screen.
 */
export function boxTotalCents(
  box: BurgerBox,
  selections: Selections,
  products: readonly BoxProduct[],
): number {
  if (box.fixedPriceCents !== null && box.fixedPriceCents > 0) {
    // Not the sum of the allocation, which would round back to the same number the long way round.
    // The box is sold for this.
    return box.fixedPriceCents
  }
  return boxLines(box, selections, products).reduce((total, line) => total + line.product.priceCents, 0)
}

/**
 * What stops the box being added.
 *
 * The first rule is not the config's: **a box has to contain something.** Every slot in this data is
 * `required: false`, so legacy's `!s.required || …` guard is vacuously true and its button adds an empty
 * group. The second rule is the config's, and only bites where a slot really is required.
 */
export function boxProblems(
  box: BurgerBox,
  selections: Selections,
  products: readonly BoxProduct[],
): string[] {
  if (boxLines(box, selections, products).length === 0) {
    return [tt('Box je prázdny - vyberte aspoň jednu položku.')]
  }
  const missing = box.slots.filter(
    (slot) =>
      slot.required &&
      (selections[slot.id] ?? null) === null &&
      fixedForSlot(slot, products).length === 0,
  )
  return missing.map((slot) => `${tt('Vyberte')}: ${slot.label}`)
}

export function boxSummary(
  box: BurgerBox,
  selections: Selections,
  products: readonly BoxProduct[],
): string {
  const lines = boxLines(box, selections, products)
  if (lines.length === 0) return tt('Zatiaľ nič nevybrané.')
  return `${lines.map((line) => line.product.name).join(', ')} - ${formatCents(boxTotalCents(box, selections, products))}`
}

/** A fresh group id. Passed in rather than read from a clock, so a test can pin it. */
export function boxGroupId(seed: string): string {
  return `box_${seed}`
}

export function boxProductOf(entry: {
  id?: number
  name?: string
  price?: unknown
  categoryId?: number
}): BoxProduct {
  return {
    id: entry.id ?? 0,
    name: entry.name ?? '',
    priceCents: parseMoneyToCents(entry.price as string | number | null),
    categoryId: entry.categoryId ?? null,
  }
}
